import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownToLine, ArrowUpToLine, CornerDownRight, MessageCircle, PlusCircle } from "lucide-react";

import type { EmailThreadSummary, EmailThreadMessage, EmailThreadComment } from "@/types/email";
import type { Task } from "@/types/projects";
import type { CrmLead } from "@/types/crm";

type ContactCandidate = {
  name?: string | null;
  email?: string | null;
};

function getPrimarySourceUrl(contact?: { source_url?: string | null; source_urls?: string[] | null } | null): string | null {
  const direct = contact?.source_url?.trim();
  if (direct) return direct;
  const list = contact?.source_urls;
  if (!Array.isArray(list)) return null;
  const first = list.find((url) => typeof url === "string" && url.trim().length > 0);
  return first ? first.trim() : null;
}

function normalizeExternalUrl(url: string | null): string | null {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

type Props = {
  thread: EmailThreadSummary | null;
  messages: EmailThreadMessage[];
  messageLoading?: boolean;
  comments: EmailThreadComment[];
  commentsLoading?: boolean;
  tasks: Task[];
  onReply?: () => void;
  onForward?: () => void;
  onCreateTask?: () => void;
  onAddComment?: (text: string) => void;
  lead?: CrmLead | null;
  contact?: {
    id: number;
    name?: string | null;
    contact_name?: string | null;
    company_name?: string | null;
    email?: string | null;
    phone?: string | null;
    website?: string | null;
    source_url?: string | null;
    source_urls?: string[];
  } | null;
  onOpenLead?: () => void;
  onOpenContact?: () => void;
  onCreateLeadFromThread?: () => void;
  onLinkToExistingLead?: () => void;
  onCreateContactFromThread?: (candidate?: ContactCandidate) => void;
  contactCandidates?: ContactCandidate[];
  contactSearchValue?: string;
  contactSearchResults?: Array<{
    id: number;
    name?: string | null;
    company_name?: string | null;
    email?: string | null;
  }>;
  contactSearching?: boolean;
  contactLinking?: boolean;
  contactLinkSuccess?: boolean;
  onContactSearchChange?: (value: string) => void;
  onLinkExistingContact?: (contactId: number) => void;
  onOpenContactResult?: (contactId: number) => void;
  onCreateEventFromThread?: () => void;
};

export function EmailThreadView({
  thread,
  messages,
  messageLoading,
  comments,
  commentsLoading,
  tasks,
  onReply,
  onForward,
  onCreateTask,
  onAddComment,
  lead,
  contact,
  onOpenLead,
  onOpenContact,
  onCreateLeadFromThread,
  onLinkToExistingLead,
  onCreateContactFromThread,
  contactCandidates,
  contactSearchValue,
  contactSearchResults,
  contactSearching,
  contactLinking,
  contactLinkSuccess,
  onContactSearchChange,
  onLinkExistingContact,
  onOpenContactResult,
  onCreateEventFromThread,
}: Props) {
  const [showFullThread, setShowFullThread] = useState(false);
  const [collapsedMessages, setCollapsedMessages] = useState<Record<string, boolean>>({});
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [selectedContactLabel, setSelectedContactLabel] = useState<string>("");
  const contactSourceUrl = useMemo(() => getPrimarySourceUrl(contact), [contact]);
  const contactSourceHref = useMemo(() => normalizeExternalUrl(contactSourceUrl), [contactSourceUrl]);
  const lastContactedDaysAgo = useMemo(() => {
    if (!thread?.last_message_at) return null;
    const last = new Date(thread.last_message_at);
    if (Number.isNaN(last.getTime())) return null;
    const diffDays = Math.round((Date.now() - last.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays;
  }, [thread?.last_message_at]);

  useEffect(() => {
    setShowFullThread(false);
  }, [thread?.id]);

  if (!thread) {
    return (
      <div className="flex h-full items-center justify-center rounded-3xl border border-dashed border-border/70 bg-background/40 p-8 text-sm text-muted-foreground">
        Select a thread to view conversation details.
      </div>
    );
  }

  const DirectionIcon =
    thread.direction === "inbound"
      ? ArrowDownToLine
      : thread.direction === "outbound"
      ? ArrowUpToLine
      : CornerDownRight;

  const visibleMessages = showFullThread ? messages : messages.slice(-1);

  return (
    <div className="flex h-full flex-col rounded-3xl border border-border bg-background/50">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
        <div>
          <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <DirectionIcon className="h-3.5 w-3.5" />
            {thread.direction === "inbound"
              ? "Inbox"
              : thread.direction === "outbound"
              ? "Sent"
              : "Inbox & Sent"}
          </p>
          <h2 className="mt-1 text-lg font-semibold text-foreground">
            {thread.subject || "(No subject)"}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {thread.participants.length ? thread.participants.join(" · ") : "Unknown participants"}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 text-xs text-muted-foreground">
          {lastContactedDaysAgo != null && (
            <p className="rounded-full border border-border bg-background/30 px-3 py-1">
              Last contacted: {lastContactedDaysAgo === 0 ? "today" : `${lastContactedDaysAgo} day${lastContactedDaysAgo === 1 ? "" : "s"} ago`}
            </p>
          )}
          {thread.followup_needed && (
            <p className="rounded-full border border-warning/60 bg-warning/10 px-3 py-1 text-warning">
              Potential follow-up opportunity
            </p>
          )}
        </div>
      </header>
      <div className="grid flex-1 grid-rows-[minmax(0,1fr)_auto] gap-4 overflow-hidden px-4 py-4 sm:px-5">
        <div className="space-y-4 overflow-y-auto pr-1">
          {!messageLoading && messages.length > 1 && (
            <div className="flex items-center justify-between rounded-xl border border-border/70 bg-background/40 px-3 py-2 text-xs text-muted-foreground">
              <span>
                {showFullThread
                  ? `Showing full thread (${messages.length} messages)`
                  : "Showing latest message only"}
              </span>
              <button
                type="button"
                onClick={() => setShowFullThread((prev) => !prev)}
                className="rounded-full border border-border bg-background/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
              >
                {showFullThread ? "Hide full thread" : ">> Show full thread"}
              </button>
            </div>
          )}
          {messageLoading ? (
            <p className="text-sm text-muted-foreground">Loading messages…</p>
          ) : messages.length === 0 ? (
            <p className="text-sm text-muted-foreground">No messages logged for this thread.</p>
          ) : (
            visibleMessages.map((message) => (
              <article
                key={message.id}
                className="aa-glass-card p-3 text-sm text-foreground"
              >
                <header className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-background/30 px-2 py-0.5">
                      {message.direction === "inbound" ? "From external" : "From you"}
                    </span>
                    <span>{message.from_address || "Unknown sender"}</span>
                    <span>→</span>
                    <span>{message.to_address || "Unknown recipient"}</span>
                  </div>
                  <span>{formatTimestamp(message.sent_at || message.created_at)}</span>
                </header>
                <h3 className="mt-2 text-sm font-semibold text-foreground">
                  {message.subject || "(No subject)"}
                </h3>
                {message.body_html && (() => {
                  const isCollapsed = collapsedMessages[message.id] ?? true;
                  return (
                    <section className="mt-3 rounded-xl border border-border bg-background/30 p-3">
                      <header className="flex items-center justify-between gap-2">
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Email content</p>
                        <button
                          type="button"
                          onClick={() =>
                            setCollapsedMessages((prev) => ({
                              ...prev,
                              [message.id]: !isCollapsed,
                            }))
                          }
                          className="rounded-full border border-border bg-background/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
                        >
                          {isCollapsed ? "Show" : "Hide"}
                        </button>
                      </header>
                      {!isCollapsed && <EmailHtmlPreview html={message.body_html} />}
                    </section>
                  );
                })()}
              </article>
            ))
          )}
          <section className="aa-glass-card space-y-3 p-3 text-xs text-foreground/70">
            <header className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <PlusCircle className="h-4 w-4 text-muted-foreground" />
              <p className="uppercase tracking-wide text-muted-foreground">Contact</p>
            </div>
              <p className="text-[11px] text-muted-foreground">Create or open CRM contacts in place</p>
            </header>
            {contactLinkSuccess && (
              <div className="rounded-full border border-emerald-400/50 bg-emerald-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-200">
                Contact linked
              </div>
            )}
            {thread.contact_id ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    {contact?.name || contact?.contact_name || contact?.company_name || `Contact #${thread.contact_id}`}
                  </p>
                  <div className="text-[11px] text-muted-foreground space-x-2">
                    {contact?.email && <span>{contact.email}</span>}
                    {contact?.phone && <span>{contact.phone}</span>}
                    {contact?.website && <span>{contact.website}</span>}
                  </div>
                  {contactSourceHref && (
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                      <span>Source URL:</span>
                      <a
                        href={contactSourceHref}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="underline decoration-primary/70 underline-offset-2 hover:text-foreground"
                      >
                        {contactSourceUrl}
                      </a>
                      <a
                        href={contactSourceHref}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="rounded-full border border-border bg-background/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
                      >
                        Go to Source URL
                      </a>
                    </div>
                  )}
                  {!contact && (
                    <p className="text-[11px] text-muted-foreground">
                      Linked to this thread for faster CRM follow-ups.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onOpenContact}
                  className="rounded-full border border-border bg-background/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
                >
                  Open in Contacts
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-xl border border-border/60 bg-background/40 px-3 py-2">
                  <header className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <PlusCircle className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="uppercase tracking-wide text-muted-foreground">Add to CRM</p>
                        <p className="text-[11px] text-muted-foreground">
                          Choose which inbox identities should be synced as CRM contacts.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onCreateContactFromThread?.()}
                      className="rounded-full border border-primary/60 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary transition-all duration-200 hover:border-primary"
                    >
                      Create contact manually
                    </button>
                  </header>
                  {contactCandidates && contactCandidates.length ? (
                    <ul className="mt-3 space-y-2">
                      {contactCandidates.map((candidate) => (
                        <li
                          key={candidate.email || candidate.name || "candidate"}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-background/60 px-2 py-2"
                        >
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-foreground">
                              {candidate.name || candidate.email || "Untitled contact"}
                            </p>
                            {candidate.email && (
                              <p className="text-[11px] text-muted-foreground">{candidate.email}</p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => onCreateContactFromThread?.(candidate)}
                            disabled={!candidate.email}
                            className="rounded-full border border-primary/60 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary transition-all duration-200 hover:border-primary disabled:cursor-not-allowed disabled:border-border disabled:text-muted-foreground"
                          >
                            {candidate.email ? "Add to CRM" : "Missing email"}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-3 text-[11px] text-muted-foreground">
                      No contact candidates yet. Wait for another inbound reply or send a follow-up to surface addresses.
                    </p>
                  )}
                </div>
                <div className="rounded-xl border border-border/60 bg-background/40 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Link existing contact</p>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="search"
                        value={contactSearchValue ?? ""}
                        onChange={(event) => {
                          setSelectedContactId(null);
                          setSelectedContactLabel("");
                          onContactSearchChange?.(event.target.value);
                        }}
                        placeholder="Search by name, email, or company…"
                        className="h-8 flex-1 rounded-lg border border-border/60 bg-background/40 px-3 text-xs text-foreground"
                      />
                      <span className="text-[11px] text-muted-foreground">
                        {contactSearching ? "Searching…" : contactSearchResults?.length ? `${contactSearchResults.length} found` : ""}
                      </span>
                    </div>
                    {Boolean(contactSearchValue?.trim()) &&
                      contactSearchResults &&
                      contactSearchResults.length > 0 && (
                        <div className="w-full rounded-lg border border-border bg-background/90 shadow-lg">
                          <ul className="max-h-48 overflow-y-auto py-1 text-xs">
                            {contactSearchResults.map((result) => (
                              <li key={result.id}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedContactId(result.id);
                                    setSelectedContactLabel(
                                      result.name || result.company_name || result.email || `Contact #${result.id}`,
                                    );
                                  }}
                                  className="w-full px-3 py-2 text-left hover:bg-background/60"
                                >
                                  <p className="font-semibold text-foreground">
                                    {result.name || result.company_name || `Contact #${result.id}`}
                                  </p>
                                  {result.email && <p className="text-[11px] text-muted-foreground">{result.email}</p>}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </div>
                  {selectedContactId && (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-background/60 px-2 py-1">
                      <p className="text-xs font-semibold text-foreground">{selectedContactLabel}</p>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => onOpenContactResult?.(selectedContactId)}
                          className="rounded-full border border-border bg-background/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          onClick={() => onLinkExistingContact?.(selectedContactId)}
                          className="rounded-full border border-border bg-background/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
                          disabled={contactLinking}
                        >
                          {contactLinking ? "Linking…" : "Link"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
          <section className="aa-glass-card space-y-3 p-3 text-xs text-foreground/70">
            <header className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <PlusCircle className="h-4 w-4 text-muted-foreground" />
                <p className="uppercase tracking-wide text-muted-foreground">Lead</p>
              </div>
            </header>
            {lead ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">{lead.title || `Lead ${lead.id}`}</p>
                  <p className="text-[11px] text-muted-foreground">
                    Stage: {lead.pipeline_stage} · Region: {lead.region || "—"}
                  </p>
                  {lead.event_window && (
                    <p className="text-[11px] text-muted-foreground">
                      Event window: {lead.event_window.start} → {lead.event_window.end}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={onOpenLead}
                    className="rounded-full border border-border bg-background/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
                  >
                    Open in Booking
                  </button>
                  <button
                    type="button"
                    onClick={onCreateEventFromThread}
                    className="rounded-full border border-primary/60 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary transition-all duration-200 hover:border-primary"
                  >
                    Add show
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">No lead linked to this thread yet.</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={onCreateLeadFromThread}
                    className="rounded-full border border-primary/60 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary transition-all duration-200 hover:border-primary"
                  >
                    Create lead from thread
                  </button>
                  <button
                    type="button"
                    onClick={onLinkToExistingLead}
                    className="rounded-full border border-border bg-background/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
                  >
                    Link to existing lead
                  </button>
                  <button
                    type="button"
                    onClick={onCreateEventFromThread}
                    className="rounded-full border border-primary/60 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary transition-all duration-200 hover:border-primary"
                  >
                    Add show
                  </button>
                </div>
              </div>
            )}
          </section>
          <section className="aa-glass-card space-y-3 p-3 text-xs text-foreground/70">
            <header className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-muted-foreground" />
                <p className="uppercase tracking-wide text-muted-foreground">Internal comments</p>
              </div>
              <p className="text-[11px] text-muted-foreground">Visible to your team only · never sent</p>
            </header>
            {commentsLoading ? (
              <p>Loading comments…</p>
            ) : comments.length === 0 ? (
              <p>No comments yet. Use this space to leave notes for teammates.</p>
            ) : (
              <ul className="space-y-2">
                {comments.map((comment) => (
                  <li
                    key={comment.id}
                    className="rounded-xl border border-border bg-background/50 px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                      <span>{comment.author_name || "Teammate"}</span>
                      <span>{formatTimestamp(comment.created_at)}</span>
                    </div>
                    <p className="mt-1 text-xs text-foreground/80 whitespace-pre-line">{comment.text}</p>
                  </li>
                ))}
              </ul>
            )}
            <CommentForm
              onSubmit={onAddComment}
            />
          </section>
          <section className="aa-glass-card space-y-3 p-3 text-xs text-foreground/70">
            <header className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <PlusCircle className="h-4 w-4 text-muted-foreground" />
                <p className="uppercase tracking-wide text-muted-foreground">Linked tasks</p>
              </div>
              <button
                type="button"
                onClick={onCreateTask}
                className="rounded-full border border-primary/60 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary transition-all duration-200 hover:border-primary"
              >
                Create task from thread
              </button>
            </header>
            {tasks.length === 0 ? (
              <p>No tasks linked yet. Create a follow-up task to track next actions.</p>
            ) : (
              <ul className="space-y-2">
                {tasks.map((task) => (
                  <li
                    key={task.id}
                    className="flex items-center justify-between gap-2 rounded-xl border border-border bg-background/50 px-3 py-2"
                  >
                    <div>
                      <p className="text-xs font-semibold text-foreground">{task.title}</p>
                      {task.due_date && (
                        <p className="text-[11px] text-muted-foreground">
                          Due {new Date(task.due_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {task.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background/30 px-3 py-2 text-xs">
          <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
            <button
              type="button"
              onClick={onReply}
              className="rounded-full border border-border bg-background/40 px-3 py-1 font-semibold text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
            >
              Reply
            </button>
            <button
              type="button"
              onClick={onForward}
              className="rounded-full border border-border bg-background/40 px-3 py-1 font-semibold text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
            >
              Forward
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Manual replies only · Email Agent features are not required for this view.
          </p>
        </footer>
      </div>
    </div>
  );
}

function EmailHtmlPreview({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState<number>(240);
  const [showExternal, setShowExternal] = useState(false);

  const sanitizedHtml = useMemo(() => {
    if (!html) return "";
    const withoutMeta = html.replace(/<meta[^>]*>/gi, "");
    const withoutContainers = withoutMeta.replace(/<\/?(html|head|body)[^>]*>/gi, "");
    const withoutScripts = withoutContainers.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      "",
    );
    const normalized = withoutScripts.replace(/src=(["'])cid:[^"']*\1/gi, 'src="data:,"');
    const withoutInlineHandlers = normalized.replace(/\son\w+=(["'])(.*?)\1/gi, "");
    const withoutEditable = withoutInlineHandlers.replace(/\scontenteditable(=(["'])(.*?)\2)?/gi, "");
    const withSafeLinks = withoutEditable.replace(/<a(\s[^>]*?)?>/gi, (match, attrs = "") => {
      let safeAttrs = attrs;
      safeAttrs = safeAttrs.replace(/\shref=(["'])\s*javascript:[^"']*\1/gi, ' href="#"');
      if (!/\starget=/.test(safeAttrs)) {
        safeAttrs += ' target="_blank"';
      }
      if (!/\srel=/.test(safeAttrs)) {
        safeAttrs += ' rel="noreferrer noopener"';
      }
      return `<a${safeAttrs}>`;
    });
    if (showExternal) return withSafeLinks;
    return withSafeLinks.replace(
      /<(img|video|audio|source|iframe|embed|object)(\s[^>]*?)?>/gi,
      (match, tag, attrs = "") => {
        if (!attrs) return `<${tag} data-blocked="true">`;
        let safeAttrs = attrs;
        safeAttrs = safeAttrs.replace(/src=(["'])(.*?)\1/gi, 'src="data:,"');
        safeAttrs = safeAttrs.replace(/srcset=(["'])(.*?)\1/gi, 'srcset=""');
        safeAttrs = safeAttrs.replace(/poster=(["'])(.*?)\1/gi, 'poster=""');
        safeAttrs = safeAttrs.replace(/\sdata-src=(["'])(.*?)\1/gi, "");
        safeAttrs = safeAttrs.replace(/\son\w+=(["'])(.*?)\1/gi, "");
        return `<${tag}${safeAttrs} data-blocked="true">`;
      },
    );
  }, [html, showExternal]);

  const srcDoc = useMemo(
    () => `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      :root { color-scheme: light; }
      body { margin: 0; font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #0f172a; background: #ffffff; cursor: default; }
      img { max-width: 100%; height: auto; }
      table { max-width: 100%; }
      a { color: #2563eb; }
      input, textarea, select, button, [contenteditable] {
        pointer-events: none !important;
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
        appearance: none !important;
        -webkit-appearance: none !important;
      }
      textarea, input { resize: none !important; }
      img[data-blocked], video[data-blocked], audio[data-blocked], iframe[data-blocked], object[data-blocked], embed[data-blocked], source[data-blocked] { display: none !important; }
    </style>
  </head>
  <body>
    ${sanitizedHtml}
  </body>
</html>`,
    [sanitizedHtml],
  );

  const updateHeight = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc) return;
    const next = Math.max(doc.body?.scrollHeight ?? 0, doc.documentElement?.scrollHeight ?? 0);
    if (next && Math.abs(next - height) > 4) {
      setHeight(next);
    }
  }, [height]);

  useEffect(() => {
    updateHeight();
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    const target = doc?.body ?? doc?.documentElement;
    if (!target || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(target);
    return () => observer.disconnect();
  }, [srcDoc, updateHeight]);

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background/40 px-3 py-2 text-[11px] text-foreground/80">
        <span>
          {showExternal
            ? "Images and external media are enabled for this preview."
            : "Images and external media are hidden to keep previews lightweight."}
        </span>
        <button
          type="button"
          onClick={() => setShowExternal((prev) => !prev)}
          className="rounded-full border border-border bg-background/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
        >
          {showExternal ? "Hide images" : "Show images"}
        </button>
      </div>
      <div className="overflow-hidden rounded-xl border border-border bg-background/40">
        <iframe
          ref={iframeRef}
          title="Email preview"
          sandbox="allow-same-origin allow-popups"
          srcDoc={srcDoc}
          onLoad={updateHeight}
          className="w-full"
          style={{ height }}
        />
      </div>
    </div>
  );
}

function CommentForm({ onSubmit }: { onSubmit?: (text: string) => void }) {
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const textarea = form.elements.namedItem("comment") as HTMLTextAreaElement | null;
    const value = textarea?.value ?? "";
    if (!value.trim()) return;
    onSubmit?.(value.trim());
    if (textarea) textarea.value = "";
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <textarea
        name="comment"
        rows={2}
        placeholder="Add an internal note for your team…"
        className="w-full resize-none rounded-lg border border-border bg-background/40 px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground"
      />
      <div className="flex justify-end">
        <button
          type="submit"
          className="rounded-full border border-border bg-background/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
        >
          Add comment
        </button>
      </div>
    </form>
  );
}

function formatTimestamp(value?: string | null) {
  if (!value) return "Unknown time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}
